import { useMemo } from 'react';

import { classicFormat } from '@/utils/hooks/format/useFormatNumber';

import type { UserNumberFormatType } from '@/types/userType';

import { SimpleHorizontalBarChartComponent } from '@/components/composite/Charts/SimpleHorizontalBarChart';

type GraphDataEntry = {
    label: string;
    value: number;
};

type VideoViewsQuartileChartProps = {
    data?: {
        graphData?: GraphDataEntry[];
    };
    userNumbersFormat?: {
        number?: UserNumberFormatType;
    };
};

/**
 * Video Views Quartile Chart
 *
 * Wrapper component that renders video playback completion rates using
 * SimpleHorizontalBarChartComponent.
 *
 * Data is pre-transformed in MediaViewerStats from:
 * - playback_0_count: Views that started (0%)
 * - playback_25_count: Views that reached 25%
 * - playback_50_count: Views that reached 50%
 * - playback_75_count: Views that reached 75%
 * - playback_100_count: Views that completed (100%)
 */
const VideoViewsQuartileChart = ({ data, userNumbersFormat }: VideoViewsQuartileChartProps) => {
    const chartData = useMemo(() => {
        return {
            graphData: data?.graphData ?? [],
            name: {
                value: 'chart.twitter.video_views_quartile_value',
                label: 'chart.twitter.video_views_quartile_label'
            },
            color: {
                value: 'blue'
            },
            valueFormat: classicFormat,
            margin: { top: 10, right: 20, left: 10, bottom: 10 },
            allowDecimals: false // Video view counts are always integers
        };
    }, [data]);

    return (
        <SimpleHorizontalBarChartComponent
            data={chartData}
            userNumbersFormat={userNumbersFormat}
            height={160}
            activeTable={false}
            isExport={false}
        />
    );
};

export default VideoViewsQuartileChart;
